import { translateBackend } from '@assets/i18n/config';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { isNil, kebabCase } from 'lodash-es';
import type { UserWithCustomFields } from '../personsOfInterest.visual';

/**
 * `disabled` for POI `TextField`s: loading, or any listed option key has `FieldRequiredType.NOT_EDITABLE`
 * (same rule as the Yes/No radios in `FieldComponent` for a single key).
 * For nested `PoiUserField`, set `parentField` to the wrapping `FieldComponent`’s `field` and call with `(loading, options, parentField, field)`.
 */
export function isPoiTextFieldDisabled(
  loading: boolean,
  options: { [k: string]: { required?: FieldRequiredType } } | undefined,
  ...fieldKeys: (keyof UserWithCustomFields | undefined)[]
): boolean {
  if (loading) {
    return true;
  }
  for (const key of fieldKeys) {
    if (
      key != null &&
      options?.[key as string]?.required === FieldRequiredType.NOT_EDITABLE
    ) {
      return true;
    }
  }
  return false;
}

type PersonsOfInterestFieldBaseType = {
  update: (newValue: any) => void;
  userData: any;
  loading: boolean;
  options: any;
  setFocused: React.Dispatch<React.SetStateAction<string[]>>;
  focused: string[];
};

type ReusablePersonOfInterestFieldType = {
  conditionToRender: boolean;
  field: keyof UserWithCustomFields;
  /** Same `field` as the wrapping `FieldComponent` — used with `isPoiTextFieldDisabled` so parent NOT_EDITABLE locks the nested text. */
  parentField?: keyof UserWithCustomFields;
  testId: string;
  focusValue: string;
};

interface PersonsOfInterestFieldType
  extends PersonsOfInterestFieldBaseType,
    ReusablePersonOfInterestFieldType {}
/**
 * @param conditionToClear
 * The condition that is used for clearing the state of nested elements. Will reset the value if the current selection and all of its conditions are false
 */

export const FieldComponent = ({
  field,
  children,
  noGrid = false,
  options,
  grid,
  userData,
  loading,
  update,
  t,
  nestedElements,
  conditionToClear = true,
  defaultOptionsToUndefined = false,
}: {
  field: keyof UserWithCustomFields;
  children?: any;
  noGrid?: boolean;
  options: any;
  grid: boolean;
  userData: any;
  loading: boolean;
  update: (newValue: any) => void;
  t: any;
  nestedElements?: string[];
  conditionToClear?: boolean;
  defaultOptionsToUndefined?: boolean;
}) => {
  if (!options?.[field]?.enabled) return <></>;

  const additionalInfoOpts = options?.[field]?.additionalInfo;
  const nestedBooleanFields = [
    'isRegisteredWithCRA',
    'receivesDonationsFromPublic',
  ];

  const value =
    defaultOptionsToUndefined && isNil(userData?.[field])
      ? undefined
      : userData?.[field]
        ? 'yes'
        : 'no';

  return (
    <Grid item xs={12} md={grid && !noGrid ? 4 : 12}>
      <RadioGroup
        testId={kebabCase(field)}
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          /* Update parent with the field's new value. If 'no', and conditionToClear with nestedElements exist, reset nestedElements to false or ''. */
          const newValue = event.target.value === 'yes';
          const updatedData = {
            ...userData,
            [field]: newValue,
            ...(event.target.value === 'no' &&
              conditionToClear &&
              nestedElements?.reduce(
                (acc, element) => ({
                  ...acc,
                  [element]: nestedBooleanFields.includes(element) ? false : '',
                }),
                {},
              )),
          };
          update(updatedData);
        }}
        disabled={loading}
        label={translateBackend(options?.[field]?.label)}
        infoTooltip={additionalInfo(additionalInfoOpts)}
      >
        <Radio
          testId={`${kebabCase(field)}-option-no`}
          value={'no'}
          disabled={
            options?.[field]?.required === FieldRequiredType.NOT_EDITABLE
          }
          label={t('shared:no')}
        />
        <Radio
          testId={`${kebabCase(field)}-option-yes`}
          value={'yes'}
          disabled={
            options?.[field]?.required === FieldRequiredType.NOT_EDITABLE
          }
          label={t('shared:yes')}
        />
      </RadioGroup>
      {children}
    </Grid>
  );
};

export const SSSymbolsField = ({
  update,
  userData,
  loading,
  options,
  setFocused,
  focused,
}: PersonsOfInterestFieldBaseType) => {
  const ssSymbols = userData.ssSymbols?.[0] ?? '';
  return (
    <TextField
      size='small'
      testId='ss-symbols'
      onChange={(e: any) =>
        update({ ...userData, ssSymbols: [e.target.value] })
      }
      disabled={isPoiTextFieldDisabled(loading, options, 'ssSymbols')}
      label={translateBackend(options?.ssSymbols?.label)}
      infoTooltip={additionalInfo(options?.ssSymbols?.additionalInfo)}
      fullWidth
      value={ssSymbols}
      onBlur={() => setFocused([...focused, 'ssSymbols'])}
      error={
        ssSymbols === '' &&
        focused.includes('ssSymbols') &&
        options?.ssSymbols?.required !== FieldRequiredType.NOT_REQUIRED
      }
    />
  );
};

export const RegulatedEntityDetails = ({
  update,
  userData,
  loading,
  options,
  setFocused,
  focused,
}: PersonsOfInterestFieldBaseType) =>
  userData.isEntityRegulated ? (
    <Grid
      item
      container
      xs={12}
      columnSpacing={2}
      rowSpacing={{ xs: 2, md: 0 }}
    >
      <Grid item flex={1}>
        {options?.regulatorName?.enabled && userData.isEntityRegulated && (
          <TextField
            size='small'
            testId='is-entity-regulated'
            onChange={(e: any) =>
              update({ ...userData, regulatorName: e.target.value })
            }
            disabled={isPoiTextFieldDisabled(loading, options, 'regulatorName')}
            label={translateBackend(options?.regulatorName?.label)}
            infoTooltip={additionalInfo(options?.regulatorName?.additionalInfo)}
            fullWidth
            value={userData.regulatorName ?? ''}
            onBlur={() => setFocused([...focused, 'regulatorName'])}
            error={
              !userData.regulatorName &&
              focused.includes('regulatorName') &&
              options?.regulatorName?.required !==
                FieldRequiredType.NOT_REQUIRED
            }
          />
        )}
      </Grid>
      {options?.firmNumber?.enabled && userData.isEntityRegulated && (
        <Grid item flex={1}>
          <TextField
            size='small'
            testId='firm-number'
            onChange={(e: any) =>
              update({ ...userData, firmNumber: e.target.value })
            }
            disabled={isPoiTextFieldDisabled(loading, options, 'firmNumber')}
            label={translateBackend(options?.firmNumber?.label)}
            infoTooltip={additionalInfo(options?.firmNumber?.additionalInfo)}
            fullWidth
            value={userData.firmNumber ?? ''}
            onBlur={() => setFocused([...focused, 'firmNumber'])}
            error={
              !userData.firmNumber &&
              focused.includes('firmNumber') &&
              options?.firmNumber?.required !== FieldRequiredType.NOT_REQUIRED
            }
          />
        </Grid>
      )}
    </Grid>
  ) : null;

export const ReportingIssuerDetails = ({
  update,
  userData,
  loading,
  options,
  setFocused,
  focused,
}: PersonsOfInterestFieldBaseType) =>
  userData.isReportingIssuer ? (
    <Grid
      item
      container
      xs={12}
      columnSpacing={2}
      rowSpacing={{ xs: 2, md: 0 }}
    >
      {options?.issuerName?.enabled && userData.isReportingIssuer && (
        <Grid item flex={1}>
          <TextField
            size='small'
            testId='issuer-name'
            onChange={(e: any) =>
              update({ ...userData, issuerName: e.target.value })
            }
            disabled={isPoiTextFieldDisabled(loading, options, 'issuerName')}
            label={translateBackend(options?.issuerName?.label)}
            infoTooltip={additionalInfo(options?.issuerName?.additionalInfo)}
            fullWidth
            value={userData.issuerName ?? ''}
            onBlur={() => setFocused([...focused, 'issuerName'])}
            error={
              !userData.issuerName &&
              focused.includes('issuerName') &&
              options?.issuerName?.required !== FieldRequiredType.NOT_REQUIRED
            }
          />
        </Grid>
      )}
      {options?.issuerExchange?.enabled && userData.isReportingIssuer && (
        <Grid item flex={1}>
          <TextField
            size='small'
            testId='issuer-exchange'
            onChange={(e: any) =>
              update({ ...userData, issuerExchange: e.target.value })
            }
            disabled={isPoiTextFieldDisabled(
              loading,
              options,
              'issuerExchange',
            )}
            label={translateBackend(options?.issuerExchange?.label)}
            infoTooltip={additionalInfo(
              options?.issuerExchange?.additionalInfo,
            )}
            fullWidth
            value={userData.issuerExchange ?? ''}
            onBlur={() => setFocused([...focused, 'issuerExchange'])}
            error={
              !userData.issuerExchange &&
              focused.includes('issuerExchange') &&
              options?.issuerExchange?.required !==
                FieldRequiredType.NOT_REQUIRED
            }
          />
        </Grid>
      )}
      {options?.issuerTicker?.enabled && userData.isReportingIssuer && (
        <Grid item flex={1}>
          <TextField
            size='small'
            testId='issuer-ticker'
            onChange={(e: any) =>
              update({ ...userData, issuerTicker: e.target.value })
            }
            disabled={isPoiTextFieldDisabled(loading, options, 'issuerTicker')}
            label={translateBackend(options?.issuerTicker?.label)}
            infoTooltip={additionalInfo(options?.issuerTicker?.additionalInfo)}
            fullWidth
            value={userData.issuerTicker ?? ''}
            onBlur={() => setFocused([...focused, 'issuerTicker'])}
            error={
              !userData.issuerTicker &&
              focused.includes('issuerTicker') &&
              options?.issuerTicker?.required !== FieldRequiredType.NOT_REQUIRED
            }
          />
        </Grid>
      )}
    </Grid>
  ) : null;

export const CharityRegistrationNumber = ({
  update,
  userData,
  loading,
  options,
  setFocused,
  focused,
}: PersonsOfInterestFieldBaseType) =>
  options?.charityRegistrationNumber?.enabled &&
  userData.isRegisteredWithCRA ? (
    <Grid item xs={12} mb={2}>
      <TextField
        size='small'
        testId='charity-registration-number'
        onChange={(e: any) =>
          update({ ...userData, charityRegistrationNumber: e.target.value })
        }
        disabled={isPoiTextFieldDisabled(
          loading,
          options,
          'charityRegistrationNumber',
        )}
        label={translateBackend(options?.charityRegistrationNumber?.label)}
        infoTooltip={additionalInfo(
          options?.charityRegistrationNumber?.additionalInfo,
        )}
        fullWidth
        value={userData.charityRegistrationNumber ?? ''}
        onBlur={() => setFocused([...focused, 'charityRegistrationNumber'])}
        error={
          !userData.charityRegistrationNumber &&
          focused.includes('charityRegistrationNumber') &&
          options?.charityRegistrationNumber?.required !==
            FieldRequiredType.NOT_REQUIRED
        }
      />
    </Grid>
  ) : null;

export const InsiderSymbols = ({
  update,
  userData,
  loading,
  options,
  setFocused,
  focused,
}: PersonsOfInterestFieldBaseType) => {
  if (!options?.insiderSymbols?.enabled || !userData.isMemberOfIiroc)
    return <></>;

  const insiderSymbols = userData.insiderSymbols?.[0] ?? '';
  return (
    <Grid item xs={12}>
      <TextField
        size='small'
        testId='insider-symbol'
        onChange={(e: any) =>
          update({ ...userData, insiderSymbols: [e.target.value] })
        }
        disabled={isPoiTextFieldDisabled(loading, options, 'insiderSymbols')}
        label={translateBackend(options?.insiderSymbols?.label)}
        infoTooltip={additionalInfo(options?.insiderSymbols?.additionalInfo)}
        fullWidth
        value={insiderSymbols}
        onBlur={() => setFocused([...focused, 'insiderSymbols'])}
        error={
          insiderSymbols === '' &&
          focused.includes('insiderSymbols') &&
          options?.insiderSymbols?.required !== FieldRequiredType.NOT_REQUIRED
        }
      />
    </Grid>
  );
};

/**
 *
 * @param conditionToRender
 * The conditions required for the field to render - the field should always be set to show on the first true instance
 */
export const PoiUserField = ({
  update,
  userData,
  loading,
  options,
  setFocused,
  focused,
  conditionToRender,
  field,
  parentField,
  testId,
  focusValue,
}: PersonsOfInterestFieldType) => {
  if (!conditionToRender) {
    return null;
  }
  return (
    <Grid item xs={12}>
      <TextField
        size='small'
        testId={testId}
        onChange={(e: any) => update({ ...userData, [field]: e.target.value })}
        disabled={isPoiTextFieldDisabled(loading, options, parentField, field)}
        label={translateBackend(options?.[field]?.label)}
        infoTooltip={additionalInfo(options?.[field]?.additionalInfo)}
        fullWidth
        value={userData[field] ?? ''}
        onBlur={() => setFocused([...focused, focusValue])}
        error={
          !userData[field] &&
          focused.includes(focusValue) &&
          options?.[field]?.required !== FieldRequiredType.NOT_REQUIRED
        }
      />
    </Grid>
  );
};
